/** Brand / tenant models (white-label storefront). */

export interface SocialLink {
  id: string;
  label: string;
  handle: string;
  icon: string;
  url: string;
}

export interface Branch {
  id: string;
  name: string;
  area: string;
  dist: number;
  eta: number;
  /**
   * Whether the branch is open for ONLINE ORDERING right now. Computed
   * server-side from the branch's local time vs its online-ordering hours —
   * never from the device clock. The client only renders this value.
   */
  open: boolean;
  /** Next opening time label when closed, e.g. "10:00 AM" (server-provided). */
  opensAt?: string | null;
  address: string;
}

export interface Brand {
  name: string;
  tagline: string;
  domain: string;
  mark: string;
  logo: string;
  /**
   * What an item with no photo shows: the admin's default item image when set
   * (a 4:3 photo), otherwise the logo.
   */
  itemImage: string;
  /** True when itemImage is the default item photo, not the logo (style it as a photo). */
  itemImageIsPhoto: boolean;
  hotline: string;
  /** Support email from the restaurant's settings (optional; hidden when unset). */
  email?: string;
  /** ISO currency code from commerce config (e.g. 'EGP'); never hardcoded in views. */
  currency: string;
  /** Rating / review count — only when the brand API provides them (else hidden). */
  rating?: number;
  ratingCount?: number;
  story: string;
  social: SocialLink[];
  branches: Branch[];
  freeDeliveryThreshold: number;
  deliveryFee: number;
  slaMinutes: number;
}

/**
 * Neutral brand used only until the real brand loads from GET /api/v1/brand
 * (or if that call fails). Intentionally empty — no sample/tenant content.
 */
export const FALLBACK_BRAND: Brand = {
  name: '',
  tagline: '',
  domain: '',
  mark: '',
  logo: '',
  itemImage: '',
  itemImageIsPhoto: false,
  hotline: '',
  currency: 'EGP',
  story: '',
  social: [],
  branches: [],
  freeDeliveryThreshold: 0,
  deliveryFee: 0,
  slaMinutes: 0,
};

/** Runtime theme (managed by the admin panel / ConfigService). */
export interface BrandTheme {
  brand: string;
  deep: string;
  l300: string;
  ink: string;
  display: string;
}
